import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, ShoppingBagIcon, MenuIcon, XIcon, ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { shopMenu, helpMenu, aboutMenu } from '../data/siteContent';
type MenuKey = 'about' | 'shop' | 'help' | null;
const EASE = [0.23, 1, 0.32, 1] as const;
export function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const [openMenu, setOpenMenu] = useState<MenuKey>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<MenuKey>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const {
    pathname,
    hash
  } = useLocation();
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
    setCartOpen(false);
    const frame = requestAnimationFrame(() => {
      if (hash) {
        const el = document.getElementById(hash.slice(1));
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
          return;
        }
      }
      window.scrollTo({
        top: 0
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);
  return <div className="min-h-screen w-full bg-paper text-ink" data-id="element-86" data-mp="1rlicsq9utgr0">
      {/* Announcement */}
      <div className="bg-coffee text-paper" data-id="element-87" data-mp="1wjtnt4bvxxat">
        <div className="mx-auto flex max-w-[1400px] items-center justify-center gap-6 px-6 py-2.5" data-id="element-88" data-mp="1wlo7kfbf7jdb">
          <p className="type-eyebrow text-[10px] text-paper/90" data-id="element-89" data-mp="zvdx34x1hhw4">
            Free nationwide delivery over R 750
          </p>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-oat/40 bg-paper/95 backdrop-blur-md" onMouseLeave={() => setOpenMenu(null)} data-id="element-90" data-mp="18t7mrx0atdab">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-8 px-6 py-5" data-id="element-91" data-mp="2k9eeyai700v6">
          <Link to="/" className="type-display shrink-0 text-2xl text-coffee md:text-[26px]" aria-label="Ecoffee Cup South Africa — home" data-id="element-92" data-mp="2p7psav7p4ti5">
            Ecoffee Cup<sup className="ml-0.5 align-super text-[10px]" data-id="element-93" data-mp="1ul8zfd020khe">®</sup>
          </Link>

          <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Main navigation" data-id="element-94" data-mp="29i9iks46j749">
            <NavLink to="/" label="Home" active={pathname === '/'} data-id="element-95" data-mp="24ix00jkccm7h" />
            <NavTrigger label="About" open={openMenu === 'about'} onEnter={() => setOpenMenu('about')} onToggle={() => setOpenMenu(openMenu === 'about' ? null : 'about')} data-id="element-96" data-mp="1gtluypvyk5nj" />
            <NavTrigger label="Shop" open={openMenu === 'shop'} onEnter={() => setOpenMenu('shop')} onToggle={() => setOpenMenu(openMenu === 'shop' ? null : 'shop')} data-id="element-97" data-mp="1qkq95ndt7mhb" />
            <NavLink to="/gifting" label="Gifting" active={pathname === '/gifting'} data-id="element-98" data-mp="t49ldowpxvkp" />
            <NavLink to="/size-guide" label="Size Guide" active={pathname === '/size-guide'} data-id="element-99" data-mp="34ql9zub8f0oc" />
            <NavLink to="/stockists" label="Stockists" active={pathname === '/stockists'} data-id="element-100" data-mp="2ut76swebk8dz" />
            <NavTrigger label="Help" open={openMenu === 'help'} onEnter={() => setOpenMenu('help')} onToggle={() => setOpenMenu(openMenu === 'help' ? null : 'help')} data-id="element-101" data-mp="2zt1uys6pmjvc" />
          </nav>

          <div className="flex items-center gap-1" data-id="element-102" data-mp="3eh6fm1pbp5gr">
            <button className="rounded-full p-2.5 text-coffee transition-colors hover:bg-oat/40" aria-label="Search" data-id="element-103" data-mp="3egemqyn41lu6">
              <SearchIcon className="h-[18px] w-[18px]" strokeWidth={1.5} data-id="element-104" data-mp="231htfcjw8ah0" />
            </button>
            <button onClick={() => setCartOpen(true)} className="rounded-full p-2.5 text-coffee transition-colors hover:bg-oat/40" aria-label="Cart" data-id="element-105" data-mp="273cvwkdk8twd">
              <ShoppingBagIcon className="h-[18px] w-[18px]" strokeWidth={1.5} data-id="element-106" data-mp="2ijirk74v0dw0" />
            </button>
            <button onClick={() => setMobileOpen(true)} className="rounded-full p-2.5 text-coffee transition-colors hover:bg-oat/40 lg:hidden" aria-label="Open menu" data-id="element-107" data-mp="2dkelvqh58yaf">
              <MenuIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-108" data-mp="368af2l7i882g" />
            </button>
          </div>
        </div>

        {/* Dropdowns — one simple pattern for both */}
        <AnimatePresence data-id="element-109" data-mp="2wawb3l2rpbr1">
          {openMenu === 'about' && <DropdownMenu key="about" items={aboutMenu} columns={1} data-id="element-110" data-mp="2wdigpf63lh6m" />}
          {openMenu === 'shop' && <DropdownMenu key="shop" items={shopMenu} columns={2} data-id="element-111" data-mp="1kwaduyb3o94k" />}
          {openMenu === 'help' && <DropdownMenu key="help" items={helpMenu} columns={1} data-id="element-112" data-mp="1gu5l01sh7giz" />}
        </AnimatePresence>
      </header>

      <main className="w-full" data-id="element-113" data-mp="3sj1zryxxdd4j">{children}</main>

      <Footer data-id="element-114" data-mp="3sg7hoe2dw4ea" />

      {/* Mobile nav */}
      <AnimatePresence data-id="element-115" data-mp="dg8vdwu3ajk">
        {mobileOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} className="fixed inset-0 z-[60] overflow-y-auto bg-paper lg:hidden" data-id="element-116" data-mp="bvhfx5tg7px">
            <div className="flex items-center justify-between px-6 py-5" data-id="element-117" data-mp="f2umiy7g0e4r">
              <span className="type-display text-2xl text-coffee" data-id="element-118" data-mp="k15ypfl7n0q5">
                Ecoffee Cup<sup className="align-super text-[10px]" data-id="element-119" data-mp="o3aq07o6rl9m">®</sup>
              </span>
              <button onClick={() => setMobileOpen(false)} className="rounded-full p-2.5 text-coffee hover:bg-oat/40" aria-label="Close menu" data-id="element-120" data-mp="t2n9qjjrgc7z">
                <XIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-121" data-mp="2uu0g7d1dms65" />
              </button>
            </div>
            <nav className="px-6 pb-16" data-id="element-122" data-mp="1z9vs952hhxf">
              <MobileLink to="/" label="Home" data-id="element-123" data-mp="20bixbzcxuvi" />
              <MobileAccordion label="About" open={mobileSection === 'about'} onToggle={() => setMobileSection(mobileSection === 'about' ? null : 'about')} data-id="element-124" data-mp="3t6cpb78do0at">
                {aboutMenu.map(i => <MobileSubLink key={i.label} to={i.to} label={i.label} data-id="element-125" data-mp="kye39dgyn3mz" />)}
              </MobileAccordion>
              <MobileAccordion label="Shop" open={mobileSection === 'shop'} onToggle={() => setMobileSection(mobileSection === 'shop' ? null : 'shop')} data-id="element-126" data-mp="g0ci8mzz4u8v">
                {shopMenu.map(i => <MobileSubLink key={i.label} to={i.to} label={i.label} data-id="element-127" data-mp="13ocbbtkrcwqm" />)}
              </MobileAccordion>
              <MobileLink to="/gifting" label="Gifting" data-id="element-128" data-mp="yrdf95hjafps" />
              <MobileLink to="/corporate-branding" label="Corporate Branding" data-id="element-129" data-mp="1ic405ywop4at" />
              <MobileLink to="/size-guide" label="Size & Drink Guide" data-id="element-130" data-mp="1n9vzvf3klupk" />
              <MobileLink to="/stockists" label="Stockists" data-id="element-131" data-mp="18dwtc8w402mj" />
              <MobileAccordion label="Help" open={mobileSection === 'help'} onToggle={() => setMobileSection(mobileSection === 'help' ? null : 'help')} data-id="element-132" data-mp="18demyjfqg80w">
                {helpMenu.map(i => <MobileSubLink key={i.label} to={i.to} label={i.label} data-id="element-133" data-mp="14de0cbihcqir" />)}
              </MobileAccordion>
            </nav>
          </motion.div>}
      </AnimatePresence>

      {/* Mock cart */}
      <AnimatePresence data-id="element-134" data-mp="ujx5d46lx5di">
        {cartOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} onClick={() => setCartOpen(false)} className="fixed inset-0 z-[60] bg-coffee/40" data-id="element-135" data-mp="gn0ifdtm081w" />
            <motion.aside initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          duration: 0.28,
          ease: EASE
        }} className="fixed right-0 top-0 z-[61] flex h-full w-full max-w-sm flex-col bg-paper p-8" aria-label="Cart" data-id="element-136" data-mp="w51hqbqbahih">
              <div className="flex items-center justify-between" data-id="element-137" data-mp="de0idmla4jye">
                <span className="type-eyebrow text-xs text-coffee" data-id="element-138" data-mp="dcpivyyv9yd3">Cart</span>
                <button onClick={() => setCartOpen(false)} aria-label="Close cart" className="rounded-full p-2 hover:bg-oat/40" data-id="element-139" data-mp="8fpdlq9uczn1">
                  <XIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-140" data-mp="3ukuej3iv3aut" />
                </button>
              </div>
              <div className="flex flex-1 flex-col items-center justify-center text-center" data-id="element-141" data-mp="3ql2bf6c8lwwu">
                <p className="type-display text-3xl text-coffee" data-id="element-142" data-mp="261yjk78837ad">
                  Your cart is empty
                </p>
                <p className="type-body mt-3 max-w-[26ch] text-sm text-ink/60" data-id="element-143" data-mp="2a668o5ilep94">
                  This is a design prototype — checkout is not connected.
                </p>
                <Link to="/shop" onClick={() => setCartOpen(false)} className="type-cta mt-8 rounded-full bg-coffee px-8 py-4 text-[11px] text-paper transition-colors hover:bg-pennant" data-id="element-144" data-mp="2k1r5zqcpv1cj">
                  Shop all cups
                </Link>
              </div>
            </motion.aside>
          </>}
      </AnimatePresence>
    </div>;
}

// ── Nav atoms ──────────────────────────────────────────────────────────────
function NavLink({
  to,
  label,
  active
}: {
  to: string;
  label: string;
  active?: boolean;
}) {
  return <Link to={to} className={`type-cta rounded-full px-3.5 py-2.5 text-[11px] transition-colors ${active ? 'text-coffee' : 'text-coffee/60 hover:text-coffee'}`} data-id="element-145" data-mp="18l9kglanqj4o">
      {label}
    </Link>;
}
function NavTrigger({
  label,
  open,
  onEnter,
  onToggle
}: {
  label: string;
  open: boolean;
  onEnter: () => void;
  onToggle: () => void;
}) {
  return <button onMouseEnter={onEnter} onFocus={onEnter} onClick={onToggle} aria-expanded={open} className={`type-cta flex items-center gap-1.5 rounded-full px-3.5 py-2.5 text-[11px] transition-colors ${open ? 'text-coffee' : 'text-coffee/60 hover:text-coffee'}`} data-id="element-146" data-mp="xplc6akbb9rk">
      {label}
      <ChevronDownIcon className={`h-3 w-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} strokeWidth={2} data-id="element-147" data-mp="9z8z6la7mmu4" />
    </button>;
}

/** One simple dropdown pattern, used by both Shop and Help. */
function DropdownMenu({
  items,
  columns
}: {
  items: {
    label: string;
    to: string;
  }[];
  columns: 1 | 2;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: -8
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -8
  }} transition={{
    duration: 0.18,
    ease: EASE
  }} className="absolute left-0 right-0 top-full border-b border-oat/40 bg-paper-warm" data-id="element-148" data-mp="1zlhduvriy9kj">
      <div className="mx-auto max-w-[1400px] px-6 py-10" data-id="element-149" data-mp="1ln193ad82t51">
        <ul className={`grid gap-x-16 gap-y-3 ${columns === 2 ? 'sm:grid-cols-2 lg:max-w-3xl' : 'lg:max-w-md'}`} data-id="element-150" data-mp="1lom2gwnsw61b">
          {items.map(i => <li key={i.label} data-id="element-151" data-mp="1bubyehphxjar">
              <Link to={i.to} className="type-display group flex items-center gap-2.5 text-2xl text-coffee transition-colors hover:text-pennant" data-id="element-152" data-mp="17ulaqmv9njag">
                {i.label}
                <ArrowRightIcon className="h-4 w-4 -translate-x-1 opacity-0 transition-[transform,opacity] duration-200 group-hover:translate-x-0 group-hover:opacity-100" strokeWidth={1.5} data-id="element-153" data-mp="xwx6957uyxf3" />
              </Link>
            </li>)}
        </ul>
      </div>
    </motion.div>;
}
function MobileLink({
  to,
  label
}: {
  to: string;
  label: string;
}) {
  return <Link to={to} className="type-display block border-b border-oat/40 py-5 text-3xl text-coffee" data-id="element-154" data-mp="aalfhaelcutd">
      {label}
    </Link>;
}
function MobileAccordion({
  label,
  open,
  onToggle,
  children
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return <div className="border-b border-oat/40" data-id="element-155" data-mp="16yrenhsquv09">
      <button onClick={onToggle} aria-expanded={open} className="type-display flex w-full items-center justify-between py-5 text-3xl text-coffee" data-id="element-156" data-mp="t8nz19oddzjs">
        {label}
        <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} data-id="element-157" data-mp="1kzk9y4qvdu4f" />
      </button>
      <AnimatePresence initial={false} data-id="element-158" data-mp="3ag66uj5s6etm">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: EASE
      }} className="overflow-hidden" data-id="element-159" data-mp="3aemqyto1e5sd">
            <div className="pb-6" data-id="element-160" data-mp="3f8h2vaxehggw">{children}</div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function MobileSubLink({
  to,
  label
}: {
  to: string;
  label: string;
}) {
  return <Link to={to} className="type-ui block py-2 text-sm text-ink/70" data-id="element-161" data-mp="2mjs9jy2l6ffp">
      {label}
    </Link>;
}

// ── Footer ─────────────────────────────────────────────────────────────────
function Footer() {
  return <footer className="bg-coffee text-paper" data-id="element-162" data-mp="2wfd7nl4javk6">
      <div className="mx-auto max-w-[1400px] px-6 py-16 md:py-20" data-id="element-163" data-mp="gpab3vying75">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr]" data-id="element-164" data-mp="grmw6hzkm6of">
          <div data-id="element-165" data-mp="bpxmb5o33z1x">
            <p className="type-display text-3xl" data-id="element-166" data-mp="bq5w2oowuo8w">
              Ecoffee Cup<sup className="align-super text-xs" data-id="element-167" data-mp="2hfrju4r7bghm">®</sup>
            </p>
            <p className="type-body mt-4 max-w-xs text-sm text-paper/60" data-id="element-168" data-mp="2mdj7gmktcgg0">
              The E stands for everyone. Bamboo fibre cups made to be carried
              every day — in Cape Town, Johannesburg, and everywhere between.
            </p>
            <Link to="/stockists" className="type-cta mt-7 inline-flex items-center gap-2 rounded-full border border-paper/25 px-6 py-3.5 text-[11px] text-paper transition-colors hover:bg-paper hover:text-coffee" data-id="element-169" data-mp="2vg2bha0c4z3u">
              Find a stockist
            </Link>
          </div>

          <FooterGroup title="Shop" links={[{
          label: 'Choose Your Story',
          to: '/stories'
        }, {
          label: 'Shop All',
          to: '/shop'
        }, {
          label: 'Shop by Collection',
          to: '/collections'
        }, {
          label: 'Gifting',
          to: '/gifting'
        }, {
          label: 'Corporate Branding',
          to: '/corporate-branding'
        }, {
          label: 'Size & Drink Guide',
          to: '/size-guide'
        }]} data-id="element-170" data-mp="3a5hxydsu2idq" />
          <FooterGroup title="Help" links={[{
          label: 'Care',
          to: '/care'
        }, {
          label: 'FAQs',
          to: '/faqs'
        }, {
          label: 'Shipping & Returns',
          to: '/shipping-returns'
        }, {
          label: 'Contact',
          to: '/contact'
        }]} data-id="element-171" data-mp="3t78q8vuv9uo1" />
          <FooterGroup title="About" links={[{
          label: 'Our Story',
          to: '/about#our-story'
        }, {
          label: 'Sustainability',
          to: '/about#sustainability'
        }, {
          label: 'Become a Stockist',
          to: '/stockists#become'
        }]} data-id="element-172" data-mp="krp1a918ayes" />
        </div>

        <div className="mt-16 flex flex-col gap-4 border-t border-paper/15 pt-8 md:flex-row md:items-center md:justify-between" data-id="element-173" data-mp="6ypmk999t88a">
          <p className="type-body text-xs text-paper/45" data-id="element-174" data-mp="ys8677aedocq">
            © {new Date().getFullYear()} Ecoffee Cup® South Africa. All rights
            reserved.
          </p>
          <div className="flex gap-6" data-id="element-175" data-mp="2t1wsdhn1ywcb">
            <span className="type-body text-xs text-paper/45" data-id="element-176" data-mp="2xzgaismvft9j">Privacy</span>
            <span className="type-body text-xs text-paper/45" data-id="element-177" data-mp="2ja0ht73s5dr7">Terms</span>
            <span className="type-body text-xs text-paper/45" data-id="element-178" data-mp="2j7xvax1u62r8">Account</span>
          </div>
        </div>
      </div>
    </footer>;
}
function FooterGroup({
  title,
  links
}: {
  title: string;
  links: {
    label: string;
    to: string;
  }[];
}) {
  return <div data-id="element-179" data-mp="2f8p0rpyz20um">
      <p className="type-eyebrow mb-5 text-[10px] text-paper/50" data-id="element-180" data-mp="2f9gka2fao84j">{title}</p>
      <ul className="space-y-3" data-id="element-181" data-mp="20bo89k8yi1v3">
        {links.map(l => <li key={l.label} data-id="element-182" data-mp="20d90h37st8ps">
            <Link to={l.to} className="type-body text-sm text-paper/75 transition-colors hover:text-paper" data-id="element-183" data-mp="259zm062dgwil">
              {l.label}
            </Link>
          </li>)}
      </ul>
    </div>;
}